import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { FileText, Lock, ShieldCheck, Save, AlertCircle, Loader2 } from 'lucide-react';

const DOC_ORDER = ['informed_consent', 'privacy_policy', 'terms_of_service'];
const DOC_ICONS = {
  informed_consent: ShieldCheck,
  privacy_policy: Lock,
  terms_of_service: FileText,
};

export default function ConsentTemplateEditor() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [templates, setTemplates] = useState({});
  const [activeKey, setActiveKey] = useState('informed_consent');
  const [editBody, setEditBody] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editVersion, setEditVersion] = useState('1.0');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/consent/templates');
      setTemplates(res.data || {});
    } catch {
      toast.error('Failed to load templates');
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    const t = templates[activeKey];
    if (t) {
      setEditTitle(t.title || '');
      setEditBody(t.body || '');
      setEditVersion(t.version || '1.0');
      setDirty(false);
    }
  }, [activeKey, templates]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Auto-bump patch version if user didn't change it
      let newVersion = editVersion;
      if (templates[activeKey]?.body !== editBody && templates[activeKey]?.version === editVersion) {
        const parts = editVersion.split('.').map(Number);
        parts[parts.length - 1] += 1;
        newVersion = parts.join('.');
      }
      await api.put('/consent/templates', {
        key: activeKey,
        title: editTitle,
        body: editBody,
        version: newVersion,
      });
      toast.success(`Saved as version ${newVersion}. Existing participants will be prompted to re-sign.`);
      setEditVersion(newVersion);
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const current = templates[activeKey];

  return (
    <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-teal-400" data-testid="consent-template-editor">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FileText size={16} className="text-teal-600" /> Legal Documents (Consent / Privacy / TOS)
        </CardTitle>
        <p className="text-xs text-muted-foreground">Edit the text participants see on first login. Saving auto-bumps the version and prompts existing users to re-sign.</p>
      </CardHeader>
      <CardContent>
        <Tabs value={activeKey} onValueChange={setActiveKey}>
          <TabsList className="grid grid-cols-3 mb-3">
            {DOC_ORDER.map(k => {
              const Icon = DOC_ICONS[k];
              return (
                <TabsTrigger key={k} value={k} className="text-xs gap-1" data-testid={`tpl-tab-${k}`}>
                  <Icon size={12} /> {k.replace('_', ' ').split(' ').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ')}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {DOC_ORDER.map(k => (
            <TabsContent key={k} value={k} className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  Current version: {current?.version || '—'}
                </Badge>
                {current?.is_default && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Using default template</Badge>}
              </div>
              <div>
                <p className="text-xs font-semibold mb-1">Title</p>
                <Input
                  value={editTitle}
                  onChange={(e) => { setEditTitle(e.target.value); setDirty(true); }}
                  className="h-9 text-sm"
                  data-testid={`tpl-title-${k}`}
                />
              </div>
              <div>
                <p className="text-xs font-semibold mb-1">Body (markdown / plain text)</p>
                <Textarea
                  value={editBody}
                  onChange={(e) => { setEditBody(e.target.value); setDirty(true); }}
                  rows={14}
                  className="text-xs font-mono"
                  data-testid={`tpl-body-${k}`}
                />
              </div>
              <div>
                <p className="text-xs font-semibold mb-1">Version (auto-bumps if body changes)</p>
                <Input
                  value={editVersion}
                  onChange={(e) => { setEditVersion(e.target.value); setDirty(true); }}
                  className="h-9 text-sm w-32"
                  data-testid={`tpl-version-${k}`}
                />
              </div>
              {dirty && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle size={12} className="text-amber-600 shrink-0 mt-0.5" />
                  <span>Saving will require all existing participants to re-sign this document the next time they log in.</span>
                </div>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-1.5"
                data-testid={`tpl-save-${k}`}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
              </Button>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
